// ─────────────── DESIGN SYSTEM (Solar-synced, 2026-04-24) ───────────────
// 150+ V1 pages import {theme, cardStyle, inputStyle, btnStyle, …} from
// this file. Previously each field was a hardcoded hex — meaning every page
// was locked to the same light palette regardless of time of day.
//
// After the solar-theme refactor, every field maps to a CSS variable that
// `../theme/ThemeProvider` updates on phase boundaries. Result: every page
// that reads `theme.surface` or `theme.navy` re-paints automatically when
// the sun moves past sunrise/sunset/golden hour/etc. No per-page edits.
//
// The `theme` keys are preserved for backward compatibility — nothing else
// in the codebase needs to change.

// Legacy raw hex constants kept here so a future page that needs the *fixed*
// Oralzy brand (print, static marketing) can still grab them. Internal UI
// should prefer the `theme.*` keys below (which now flow through CSS vars).
export const BRAND = {
  orange:      '#F57C1F',
  orangeDark:  '#DC6A0F',
  orangeLight: '#FEF0E1',
  blue:        '#29A8E0',
  blueDark:    '#1F8CBC',
  blueLight:   '#E1F2FB',
};

export const theme = {
  // ── Brand (accent) — now phase-aware. Primary tracks the solar accent so
  // buttons/icons shift hue with time. If a page needs the fixed Oralzy
  // orange, import BRAND.orange instead. ─────────────────────────────────
  primary:      'var(--color-accent)',
  primaryDark:  'var(--color-accent-border)',
  primaryLight: 'var(--color-accent-soft)',

  // Secondary accent — kept distinct using a subtler variant.
  secondary:      'var(--color-accent)',
  secondaryDark:  'var(--color-accent-border)',
  secondaryLight: 'var(--color-accent-soft)',

  navy:         'var(--color-text)',
  navyLight:    'var(--color-text-secondary)',

  // Neutrals
  bg:          'var(--color-bg)',
  surface:     'var(--color-bg-card)',
  border:      'var(--color-border)',
  borderLight: 'var(--color-border)',
  textMuted:   'var(--color-text-secondary)',
  textSubtle:  'var(--color-text-tertiary)',

  // Semantic — these rotate palette-by-palette so their vibe shifts with time.
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger:  'var(--color-danger)',
  info:    'var(--color-accent)',
  purple:  'var(--color-accent)',
  pink:    'var(--color-accent)',

  // Semantic soft backgrounds (for tinted fills)
  successSoft: 'var(--color-success-soft)',
  warningSoft: 'var(--color-warning-soft)',
  dangerSoft:  'var(--color-danger-soft)',

  // Effects — shadow tracks palette so dark phases get deeper shadows.
  shadowSm: '0 1px 2px rgba(0,0,0,0.04)',
  shadow:   'var(--color-shadow)',
  shadowMd: 'var(--color-shadow)',
  shadowLg: 'var(--color-shadow)',

  // Radii (unchanged — not theme-dependent)
  radiusSm: '6px',
  radius:   '10px',
  radiusLg: '14px',

  // ── Swipe-style pastel section backgrounds ──
  // These were pure pastel fills in V1. We now map them onto `bgInset` so
  // all section cards share the current palette. Borders fall back to
  // --color-border so dark phases don't show bright pastel outlines.
  sectionBlue:  'var(--color-bg-inset)',
  sectionMint:  'var(--color-bg-inset)',
  sectionCream: 'var(--color-bg-inset)',
  sectionPink:  'var(--color-bg-inset)',
  sectionLilac: 'var(--color-bg-inset)',

  sectionBlueBorder:  'var(--color-border)',
  sectionMintBorder:  'var(--color-border)',
  sectionCreamBorder: 'var(--color-border)',
  sectionPinkBorder:  'var(--color-border)',
  sectionLilacBorder: 'var(--color-border)',

  // Sticky bar shadow
  stickyShadow:    'var(--color-shadow)',
  stickyShadowTop: 'var(--color-shadow)',
};

export const navItem = {
  display: 'flex', alignItems: 'center', gap: '12px',
  cursor: 'pointer', padding: '11px 14px',
  borderRadius: '10px', fontWeight: '500',
  fontSize: '0.9rem',
  transition: 'all 0.15s ease',
  color: 'var(--color-text-secondary)',
};

export const cardStyle = {
  backgroundColor: 'var(--color-bg-card)',
  padding: '24px',
  borderRadius: '14px',
  boxShadow: 'var(--color-shadow)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
};

export const inputStyle = {
  padding: '10px 14px',
  borderRadius: '6px',
  border: '1px solid var(--color-border)',
  fontSize: '0.9rem',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-inset)',
  color: 'var(--color-text)',
  transition: 'all 0.15s ease',
};

export const btnStyle = {
  marginTop: '20px',
  padding: '11px 22px',
  backgroundColor: 'var(--color-accent)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontWeight: '600',
  fontSize: '0.88rem',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
};

export const settingBox = {
  padding: '20px',
  border: '1px solid var(--color-border)',
  borderRadius: '10px',
  backgroundColor: 'var(--color-bg-card)',
  color: 'var(--color-text)',
  marginBottom: '15px',
};

export const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '0.78rem',
  color: 'var(--color-text-secondary)',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

export const tdStyle = {
  padding: '12px 16px',
  color: 'var(--color-text)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: '0.88rem',
};

export const thStyle = {
  padding: '11px 16px',
  borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)',
  textAlign: 'left',
  fontSize: '0.74rem',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  backgroundColor: 'var(--color-bg-inset)',
};

export const statCard = {
  backgroundColor: 'var(--color-bg-card)',
  padding: '20px 22px',
  borderRadius: '14px',
  boxShadow: 'var(--color-shadow)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};
